// Round 5c (2026-09-10) — Home "오늘 방송 예정" REAL data connection.
// § UI layout / rolling carousel / row card design are LOCKED (Round 5b).
// The only functional change: MERGE INTERNAL SNS `/home/today-schedule`
// with EXTERNAL `/schedule/external?date=today` (Naver + Kakao) UPCOMING
// rows, filter mock/test broadcasts, sort by scheduled_at ASC, and render
// each row through its existing card component (ScheduleListItem vs
// ExternalScheduleCard) — no new card, no new API, no DB write.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Clock, ChevronRight, CalendarOff, ChevronUp, ChevronDown, Pause, Play } from 'lucide-react';
import api from '@/lib/api';
import ScheduleListItem from '@/components/ScheduleListItem';
import ExternalScheduleCard, { isMockExternalRow } from '@/components/ExternalScheduleCard';
import { getBroadcastPhase } from '@/lib/broadcastStatus';

const VISIBLE = 5;              // rows shown at once
const AUTO_INTERVAL_MS = 4500;  // 4~5s per row
const RESUME_DELAY_MS = 9000;   // resume auto after 8~10s idle
const TRANSITION_MS = 400;      // animation duration

// Round 5c · Mirrors Schedule.js VISIBLE_EXTERNAL_PLATFORMS · UI allow-list.
const VISIBLE_EXTERNAL_PLATFORMS = ['naver_shopping_live', 'kakao_shopping_live'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Home page — "오늘 방송 예정" vertical rolling carousel.
 *
 * Row source (Round 5c):
 *   · INTERNAL SNS  (`/home/today-schedule`)   → ScheduleListItem
 *   · EXTERNAL       (`/schedule/external`)     → ExternalScheduleCard
 *                                                 (Naver/Kakao · UPCOMING only)
 *
 * When items > VISIBLE: auto-scrolls one row every 4.5s in an infinite loop.
 *                       Manual controls: ▲▼ buttons, wheel, keyboard, swipe.
 *                       Auto pauses on hover / focus / tab-hidden / prefers-reduced-motion.
 */
const TodayScheduleList = ({ limit = 24 }) => {
  const [internal, setInternal] = useState([]);
  const [externals, setExternals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [rowH, setRowH] = useState(88);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const resumeTimerRef = useRef(null);

  // Fetch both feeds in parallel — a failure on one MUST NOT block the other.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [iRes, eRes] = await Promise.allSettled([
        api.get('/home/today-schedule', { params: { range: 'today', limit } }),
        api.get('/schedule/external',   { params: { date: today, limit: 200 } }),
      ]);
      if (cancelled) return;
      setInternal(iRes.status === 'fulfilled' ? (iRes.value.data || []) : []);
      setExternals(eRes.status === 'fulfilled' ? (eRes.value.data?.items || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [limit]);

  // Round 5c · merge · filter · sort ASC · mock-exclusion.
  const sortedItems = useMemo(() => {
    // INTERNAL — UPCOMING only (exclude LIVE/ENDED · endpoint already drops ended).
    const iRows = (internal || [])
      .filter((r) => getBroadcastPhase(r) === 'before')
      .map((r) => ({ ...r, _kind: 'internal' }));
    // EXTERNAL — UPCOMING only · visible platforms · mock exclusion.
    const eRows = (externals || [])
      .filter((r) => VISIBLE_EXTERNAL_PLATFORMS.includes(r.platform))
      .filter((r) => (r.status || '').toUpperCase() === 'UPCOMING')
      .filter((r) => !isMockExternalRow(r))
      .map((r) => ({ ...r, _kind: 'external' }));
    // scheduled_at ASC (earliest first) — never round-robin the mix so time
    // ordering stays intact even if one platform dominates.
    return [...iRows, ...eRows].sort(
      (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)
    );
  }, [internal, externals]);

  const rollingEnabled = sortedItems.length > VISIBLE && !prefersReducedMotion();

  // Measure the first row height so translateY matches the exact row.
  useEffect(() => {
    if (!listRef.current) return;
    const first = listRef.current.querySelector('[data-role="row-slot"]');
    if (first) {
      const h = first.getBoundingClientRect().height;
      if (h > 0) setRowH(h);
    }
  }, [sortedItems, loading]);

  useEffect(() => {
    if (!rollingEnabled || isPaused || manualPause) return;
    const t = setInterval(() => setIndex((i) => i + 1), AUTO_INTERVAL_MS);
    return () => clearInterval(t);
  }, [rollingEnabled, isPaused, manualPause]);

  useEffect(() => {
    if (!rollingEnabled) return;
    if (index >= sortedItems.length) {
      const t = setTimeout(() => {
        setIsTransitioning(false);
        setIndex(0);
        requestAnimationFrame(() => requestAnimationFrame(() => setIsTransitioning(true)));
      }, TRANSITION_MS);
      return () => clearTimeout(t);
    }
    if (index < 0) {
      const t = setTimeout(() => {
        setIsTransitioning(false);
        setIndex(sortedItems.length - 1);
        requestAnimationFrame(() => requestAnimationFrame(() => setIsTransitioning(true)));
      }, TRANSITION_MS);
      return () => clearTimeout(t);
    }
  }, [index, sortedItems.length, rollingEnabled]);

  useEffect(() => {
    const onVis = () => setIsPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const scheduleResume = useCallback(() => {
    setManualPause(true);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setManualPause(false), RESUME_DELAY_MS);
  }, []);

  const go = useCallback((delta) => {
    setIndex((i) => i + delta);
    scheduleResume();
  }, [scheduleResume]);

  const onWheel = (e) => {
    if (!rollingEnabled) return;
    if (Math.abs(e.deltaY) < 4) return;
    e.preventDefault();
    go(e.deltaY > 0 ? 1 : -1);
  };
  const onKeyDown = (e) => {
    if (!rollingEnabled) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); go(-1); }
    else if (e.key === ' ' || e.key === 'Enter') {
      if (e.target === wrapperRef.current) { e.preventDefault(); setManualPause((p) => !p); }
    }
  };
  const touchRef = useRef({ y0: null, y: null });
  const onTouchStart = (e) => { touchRef.current.y0 = e.touches[0].clientY; touchRef.current.y = e.touches[0].clientY; };
  const onTouchMove = (e) => { touchRef.current.y = e.touches[0].clientY; };
  const onTouchEnd = () => {
    const { y0, y } = touchRef.current;
    if (y0 == null || y == null) return;
    const dy = y0 - y;
    if (Math.abs(dy) > 30 && rollingEnabled) go(dy > 0 ? 1 : -1);
    touchRef.current = { y0: null, y: null };
  };

  const displayRows = rollingEnabled
    ? [...sortedItems, ...sortedItems.slice(0, VISIBLE)]
    : sortedItems;

  const positionLabel = rollingEnabled
    ? `${((index % sortedItems.length) + sortedItems.length) % sortedItems.length + 1} / ${sortedItems.length}`
    : `총 ${sortedItems.length}개`;

  const renderRow = (row, i) => {
    if (row._kind === 'external') {
      return (
        <div key={`ext-${row.external_id}-${i}`} data-role="row-slot">
          <ExternalScheduleCard item={row} />
        </div>
      );
    }
    return (
      <div key={`int-${row.schedule_id}-${i}`} data-role="row-slot">
        <ScheduleListItem live={row} rowLinkMode="seller" />
      </div>
    );
  };

  return (
    <section className="py-10 border-b border-black/5 dark:border-white/10" data-testid="today-schedule-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Clock className="text-[#FF3B30]" size={22} />
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="today-schedule-title"
            >
              오늘 방송 예정
            </h2>
            {sortedItems.length > 0 && (
              <span className="ml-1 text-[11px] text-gray-400" data-testid="today-schedule-position">
                {positionLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {rollingEnabled && (
              <div className="flex items-center gap-1" role="group" aria-label="편성표 이동">
                <button
                  type="button" onClick={(e) => { e.stopPropagation(); go(-1); }}
                  className="hidden sm:inline-flex p-1.5 rounded-full border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  aria-label="이전 방송" data-testid="today-schedule-prev"
                ><ChevronUp size={14} /></button>
                <button
                  type="button" onClick={(e) => { e.stopPropagation(); go(1); }}
                  className="hidden sm:inline-flex p-1.5 rounded-full border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  aria-label="다음 방송" data-testid="today-schedule-next"
                ><ChevronDown size={14} /></button>
                <button
                  type="button" onClick={(e) => { e.stopPropagation(); setManualPause((p) => !p); }}
                  className="p-1.5 rounded-full border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  aria-label={manualPause ? '자동 롤링 재생' : '자동 롤링 정지'}
                  data-testid="today-schedule-pause"
                  aria-pressed={manualPause}
                >{manualPause ? <Play size={14} /> : <Pause size={14} />}</button>
              </div>
            )}
            <Link
              to="/schedule"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-sm font-bold text-[#FF3B30] hover:underline"
              data-testid="view-full-schedule"
            >
              전체 편성표 보기 <ChevronRight size={14} />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-400 py-6" data-testid="today-schedule-loading">불러오는 중…</div>
        ) : sortedItems.length > 0 ? (
          <div
            ref={wrapperRef}
            className={rollingEnabled ? 'relative overflow-hidden focus:outline-none' : ''}
            style={rollingEnabled ? { height: `${rowH * VISIBLE + 10 * (VISIBLE - 1)}px` } : undefined}
            onMouseEnter={() => rollingEnabled && setIsPaused(true)}
            onMouseLeave={() => rollingEnabled && setIsPaused(false)}
            onFocus={() => rollingEnabled && setIsPaused(true)}
            onBlur={() => rollingEnabled && setIsPaused(false)}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            tabIndex={rollingEnabled ? 0 : -1}
            role={rollingEnabled ? 'region' : undefined}
            aria-label={rollingEnabled ? '오늘 방송 자동 롤링 캐러셀' : undefined}
            aria-roledescription={rollingEnabled ? 'carousel' : undefined}
            data-testid="today-schedule-list"
          >
            <div
              ref={listRef}
              className="flex flex-col gap-2 sm:gap-2.5"
              style={rollingEnabled ? {
                transform: `translateY(-${index * (rowH + 10)}px)`,
                transition: isTransitioning ? `transform ${TRANSITION_MS}ms cubic-bezier(.4,.0,.2,1)` : 'none',
                willChange: 'transform',
              } : undefined}
            >
              {displayRows.map((row, i) => renderRow(row, i))}
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-gray-500 dark:text-gray-400 bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10"
            data-testid="today-schedule-empty"
          >
            <CalendarOff size={22} className="text-gray-400" />
            <span>오늘 예정된 방송이 없습니다.</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default TodayScheduleList;
